import cardStyle from "../../styles/CardCategory.module.css"
import React, { useEffect, useState } from "react"
import GetSpace from "../../utils/space"
import style from "./ElementOrder.module.css"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ButtonIcon } from "../Button/Button"
import {
  faCalendar,
  faCaretDown,
  faClock,
  faFile,
  faMinus,
  faPlus,
  faPlusSquare,
  faTrash,
  faTriangleExclamation,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons"
import { addtionalMemory, employeeMemory } from "../../Objects/Memory/Memory"
import { AddtionalEntity } from "../../Objects/Addtional/AddtionalEntity"
import { CardEmployeeRegisterInLine } from "../CardEmployeeRegister/CardEmployeeRegister"
import InputField from "../InputField/InputField"
import { GenericOrderFood } from "../../Objects/Order/GenericOrderFood"
import { TitleChildrenPath } from "../Titles/Titles"
import { convertToLocalMoney, resolveImage, setDelay } from "../../Objects/Generics/Helps"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { ListSelections } from "../ListSelections/ListSelections"
import { GenericOrderFoodAdditionalEntity } from "../../Objects/Order/GenericOrderFoodAdditionalEntity"
import { BasicCard } from "../BasicCard/BasicCard"
import iconMeal from "../../assets/images/meal.jpg"
import { useTranslation } from "react-i18next"
import { ListItem } from "../List/List"
import { CalculatePromotions } from "../../Objects/Calculates/CalculatePromotions"
import { SelectionEntity } from "../../Objects/Selection/SelectionEntity"
import { SelectionOptionEntity } from "../../Objects/Selection/SelectionOptionEntity"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import {
  normalizeReservationDateForInput,
  normalizeReservationTimeForInput,
} from "../../Extensions/ReservationExtensions"
import { SimpleToggle } from "../SimpleToggle/SimpleToggle"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { AdditionalSelectionModal } from "./AdditionalSelectionModal"

interface ElementOrderProps {
  employeeStringId: string
  title: string
  icon: IconProp
  date: string
  isClosed: boolean
  iconColor?: string
  addtionalsItems: ListItem[]
  allowedAddtionals: AddtionalEntity[]
  selectionsItems: SelectionEntity[]
  genericOrderMeal: GenericOrderFood
  urlImage: string
  calculatePromotions: CalculatePromotions
  disable: boolean
  onRemove: () => void
  onEditeAddtional: () => void
}

type SelectedOrderSelection = {
  selectionEntityId: number
  selectionOptionEntityId: number
  value?: number
  baseValue?: number
  isFree?: boolean
  selectionEntity?: {
    id?: number
    name?: string
  }
  selectionOptionEntity?: {
    id?: number
    name?: string
    value?: number
    isFree?: boolean
    allowMultiple?: boolean
    maximumSelectionsLimit?: number
  }
}

const resolveSelectionValues = <T extends SelectedOrderSelection>(selection: SelectionEntity, selectedItems: T[]) => {
  let freeRemaining = Math.max(0, selection.freeOptionsLimit ?? 0)

  return selectedItems.map((item) => {
    if (item.selectionEntityId !== selection.id) {
      return item
    }

    const baseValue = Number(item.selectionOptionEntity?.value ?? item.baseValue ?? item.value ?? 0)

    const isFree = Boolean(item.selectionOptionEntity?.isFree ?? item.isFree ?? baseValue <= 0)

    const value = isFree || freeRemaining > 0 ? 0 : baseValue

    if (!isFree && freeRemaining > 0) {
      freeRemaining -= 1
    }

    return { ...item, baseValue, isFree, value }
  })
}

const buildSelectionStatusMessages = <T extends SelectedOrderSelection>(
  selection: SelectionEntity,
  selectedItems: T[],
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const selectedCount = selectedItems.filter((item) => item.selectionEntityId === selection.id).length

  const requiredOptionsCount = Math.max(1, selection.requiredOptionsCount ?? 1)

  const freeOptionsLimit = Math.max(0, selection.freeOptionsLimit ?? 0)

  const freeUsed = selectedItems.filter(
    (item) => item.selectionEntityId === selection.id && !item.isFree && Number(item.value ?? 0) <= 0,
  ).length

  const freeRemaining = Math.max(0, freeOptionsLimit - freeUsed)

  const maximumLimitCandidates = [
    selection.hasMaximumSelectionsLimit ? Math.max(1, selection.maximumSelectionsLimit ?? 1) : undefined,
  ].filter((limit): limit is number => typeof limit === "number")

  const effectiveMaximumSelectionsLimit =
    maximumLimitCandidates.length > 0 ? Math.min(...maximumLimitCandidates) : undefined

  const maximumRemaining =
    effectiveMaximumSelectionsLimit === undefined ? 0 : Math.max(0, effectiveMaximumSelectionsLimit - selectedCount)

  const messages = [
    {
      text: selection.isRequired
        ? t("order:label_selected_required_progress", {
            selected: Math.min(selectedCount, requiredOptionsCount),
            total: requiredOptionsCount,
          })
        : t("order:label_selected_optional_progress", { count: selectedCount }),
      className:
        selection.isRequired && selectedCount >= requiredOptionsCount
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-sky-200 bg-sky-50 text-sky-700",
    },
  ]

  if (freeOptionsLimit > 0) {
    messages.push({
      text: t("order:label_free_choices_remaining", { count: freeRemaining }),
      className:
        freeRemaining > 0 ? "border-lime-200 bg-lime-50 text-lime-700" : "border-amber-200 bg-amber-50 text-amber-700",
    })
  }

  if (effectiveMaximumSelectionsLimit !== undefined) {
    messages.push(
      maximumRemaining > 0
        ? {
            text: t("order:label_maximum_choices_remaining", { count: maximumRemaining }),
            className: "border-violet-200 bg-violet-50 text-violet-700",
          }
        : {
            text: t("order:label_maximum_choices_reached"),
            className: "border-rose-200 bg-rose-50 text-rose-700",
          },
    )
  }

  return messages
}

export const ElementOrder: React.FC<ElementOrderProps> = ({
  employeeStringId,
  title,
  genericOrderMeal,
  date,
  urlImage,
  isClosed,
  addtionalsItems,
  allowedAddtionals,
  selectionsItems,
  disable,
  calculatePromotions,
  onRemove,
  onEditeAddtional,
}) => {
  const { t } = useTranslation()

  const [description, setDescription] = useState(genericOrderMeal.description || "")

  const [isReservation, setIsReservation] = useState(genericOrderMeal.isReservation ?? false)

  const [reservationDate, setReservationDate] = useState(
    normalizeReservationDateForInput(genericOrderMeal.reservationDate),
  )

  const [reservationTime, setReservationTime] = useState(
    normalizeReservationTimeForInput(genericOrderMeal.reservationTime),
  )

  const [enableList, setEnableList] = useState(false)

  const [enableSelectionList, setEnableSelectionList] = useState(false)

  const [addtional, setAddtional] = useState<GenericOrderFoodAdditionalEntity[]>(genericOrderMeal.addtionals)

  const [selections, setSelections] = useState<SelectedOrderSelection[]>(genericOrderMeal.selections ?? [])

  const [originalSelections] = useState<SelectedOrderSelection[]>(() => genericOrderMeal.selections ?? [])

  const [originalAdditionalAmounts] = useState<Record<number, number>>(() =>
    genericOrderMeal.addtionals.reduce<Record<number, number>>((acc, item) => {
      acc[item.addtionalEntityId] = (acc[item.addtionalEntityId] ?? 0) + Math.max(0, item.amount)
      return acc
    }, {}),
  )

  const [disableEdition] = useState(disable || isClosed)

  const supportsReservation = genericOrderMeal.supportsReservation()

  const missingRequiredSelections = selectionsItems.filter(
    (selection) =>
      selection.status === EEnableStatusType.Active &&
      selection.isRequired &&
      selections.filter((current) => current.selectionEntityId === selection.id).length <
        Math.max(1, selection.requiredOptionsCount ?? 1),
  )

  const hasMissingRequiredSelections = missingRequiredSelections.length > 0

  useEffect(() => {
    if (!supportsReservation && isReservation) {
      setIsReservation(false)
      syncDescription(description, false, "", "")
      setReservationDate("")
      setReservationTime("")
    }
  }, [description, isReservation, supportsReservation])

  const syncDescription = (
    nextNotes: string,
    nextIsReservation = isReservation,
    nextDate = reservationDate,
    nextTime = reservationTime,
  ) => {
    genericOrderMeal.setDescription(nextNotes)
    genericOrderMeal.setReservationInfo(nextIsReservation, nextDate, nextTime)
    setDescription(nextNotes)
  }

  const handleChange = (e: string) => syncDescription(e)

  const getAdditionalAmount = (additionalId: number) =>
    addtional
      .filter((item) => item.addtionalEntityId === additionalId)
      .reduce((sum, item) => sum + Math.max(0, item.amount), 0)

  const isAdditionalStockLimitReached = (additionalId: number, currentAmount: number) => {
    const productAdditional = allowedAddtionals.find((item) => item.id === additionalId)

    if (!productAdditional?.hasStockControl) {
      return false
    }

    const availableStockForThisItem = productAdditional.stockQuantity + (originalAdditionalAmounts[additionalId] ?? 0)

    return currentAmount >= availableStockForThisItem
  }

  const createAdditionalOrderItem = (additionalId: number, amount: number) => {
    const addtionalEntity = addtionalMemory.findById(additionalId)

    if (!addtionalEntity || addtionalEntity.status !== EEnableStatusType.Active || amount <= 0) {
      return null
    }

    const valueUnique = parseFloat(addtionalEntity.value)

    return new GenericOrderFoodAdditionalEntity({
      id: -1,
      genericOrderFoodId: genericOrderMeal.id,
      addtionalEntityId: addtionalEntity.id,
      amount,
      addtionalEntity: addtionalEntity,
      valueUnique,
      valueTotal: valueUnique * amount,
    })
  }

  const syncAdditionals = async (nextAdditionals: GenericOrderFoodAdditionalEntity[]) => {
    genericOrderMeal.addtionals = nextAdditionals

    setAddtional(nextAdditionals)

    await setDelay(50)

    onEditeAddtional()
  }

  const handleSetAdditionalAmount = async (additionalId: number, nextAmount: number) => {
    const safeAmount = Math.max(0, nextAmount)

    const currentAmount = getAdditionalAmount(additionalId)

    if (safeAmount > currentAmount && isAdditionalStockLimitReached(additionalId, currentAmount)) {
      return
    }

    const currentWithoutAdditional = genericOrderMeal.addtionals.filter(
      (item) => item.addtionalEntityId !== additionalId,
    )

    const nextAdditional = createAdditionalOrderItem(additionalId, safeAmount)

    const nextAdditionals = nextAdditional ? [...currentWithoutAdditional, nextAdditional] : currentWithoutAdditional

    await syncAdditionals(nextAdditionals)
  }

  const handleAddAddtional = async (e: number) => {
    const addtionalEntity = addtionalMemory.findById(e)

    if (!addtionalEntity) {
      return
    }

    if (addtionalEntity.status !== EEnableStatusType.Active) {
      return
    }

    await handleSetAdditionalAmount(e, getAdditionalAmount(e) + 1)
  }

  const handleIncreaseAmount = async (e: GenericOrderFoodAdditionalEntity) => {
    if (isAdditionalStockLimitReached(e.addtionalEntityId, getAdditionalAmount(e.addtionalEntityId))) {
      return
    }

    e.amount++
    e.calculate()

    setAddtional(genericOrderMeal.addtionals)

    await setDelay(50)

    onEditeAddtional()
  }

  const handleDecreaseAmount = async (e: GenericOrderFoodAdditionalEntity) => {
    e.amount--
    e.calculate()

    if (e.amount <= 0) {
      handleRemove(e)

      return
    }

    setAddtional(genericOrderMeal.addtionals.filter((e) => e))

    await setDelay(50)

    onEditeAddtional()
  }

  const imagen = resolveImage(urlImage)

  const handleRemove = async (e: GenericOrderFoodAdditionalEntity) => {
    setAddtional((last) => {
      var data = last.filter((ss) => ss !== e)

      genericOrderMeal.addtionals = data

      return data
    })

    await setDelay(50)

    onEditeAddtional()
  }

  const handleAddSelectionOption = async (selectedSelection: SelectionEntity, optionId: number) => {
    if (selectedSelection.status !== EEnableStatusType.Active) {
      return
    }

    const selectedOption: SelectionOptionEntity | undefined = selectedSelection.options.find(
      (option) => option.id === optionId,
    )

    if (!selectedOption) {
      return
    }

    const oldSelections = genericOrderMeal.selections ?? []

    const currentSelectionItems = oldSelections.filter((e) => e.selectionEntityId === selectedSelection.id)

    const requiredOptionsCount = Math.max(1, selectedSelection.requiredOptionsCount ?? 1)

    const maximumLimitCandidates = [
      selectedSelection.hasMaximumSelectionsLimit
        ? Math.max(1, selectedSelection.maximumSelectionsLimit ?? 1)
        : undefined,
    ].filter((limit): limit is number => typeof limit === "number")

    const effectiveMaximumSelectionsLimit =
      maximumLimitCandidates.length > 0 ? Math.min(...maximumLimitCandidates) : undefined

    const currentOptionItems = oldSelections.filter(
      (e) => e.selectionEntityId === selectedSelection.id && e.selectionOptionEntityId === selectedOption.id,
    )

    const optionAllowsQuantity = selectedSelection.allowMultiple && selectedOption.allowMultiple

    const optionMaximumSelectionsLimit = optionAllowsQuantity
      ? Math.max(1, selectedOption.maximumSelectionsLimit ?? 1)
      : 1

    const filtered = selectedSelection.allowMultiple
      ? oldSelections.filter(
          (e) => !(e.selectionEntityId === selectedSelection.id && e.selectionOptionEntityId === selectedOption.id),
        )
      : oldSelections.filter((e) => e.selectionEntityId !== selectedSelection.id)

    const selectionData = {
      id: -1,
      genericOrderFoodId: genericOrderMeal.id,
      selectionEntityId: selectedSelection.id,
      selectionOptionEntityId: selectedOption.id,
      value: selectedOption.isFree ? 0 : Number(selectedOption.value ?? 0),
      baseValue: selectedOption.isFree ? 0 : Number(selectedOption.value ?? 0),
      isFree: selectedOption.isFree,
      selectionEntity: {
        id: selectedSelection.id,
        name: selectedSelection.name,
      },
      selectionOptionEntity: {
        id: selectedOption.id,
        name: selectedOption.name,
        value: selectedOption.value,
        isFree: selectedOption.isFree,
        allowMultiple: selectedOption.allowMultiple,
        maximumSelectionsLimit: selectedOption.maximumSelectionsLimit,
      },
    }

    const alreadySelected = oldSelections.some(
      (e) => e.selectionEntityId === selectedSelection.id && e.selectionOptionEntityId === selectedOption.id,
    )

    if (optionAllowsQuantity) {
      if (
        effectiveMaximumSelectionsLimit !== undefined &&
        currentSelectionItems.length >= effectiveMaximumSelectionsLimit
      ) {
        return
      }

      if (currentOptionItems.length >= optionMaximumSelectionsLimit) {
        return
      }

      const updatedSelections = resolveSelectionValues(selectedSelection, [...oldSelections, selectionData])

      genericOrderMeal.selections = updatedSelections
      setSelections(updatedSelections)

      await setDelay(50)

      onEditeAddtional()

      return
    }

    if (
      selectedSelection.allowMultiple &&
      !alreadySelected &&
      effectiveMaximumSelectionsLimit !== undefined &&
      currentSelectionItems.length >= effectiveMaximumSelectionsLimit
    ) {
      return
    }

    const updatedSelections = resolveSelectionValues(
      selectedSelection,
      selectedSelection.allowMultiple && alreadySelected ? filtered : [...filtered, selectionData],
    )

    genericOrderMeal.selections = updatedSelections
    setSelections(updatedSelections)

    await setDelay(50)

    onEditeAddtional()
  }

  const handleRemoveSelection = async (selectionEntityId: number, selectionOptionEntityId: number) => {
    const currentSelections = genericOrderMeal.selections ?? selections

    const removeIndex = currentSelections.findIndex(
      (e) => e.selectionEntityId === selectionEntityId && e.selectionOptionEntityId === selectionOptionEntityId,
    )

    if (removeIndex < 0) {
      return
    }

    const updatedSelections = currentSelections.filter((_, index) => index !== removeIndex)

    const selectedSelection = selectionsItems.find((selection) => selection.id === selectionEntityId)

    const resolvedSelections = selectedSelection
      ? resolveSelectionValues(selectedSelection, updatedSelections)
      : updatedSelections

    genericOrderMeal.selections = resolvedSelections
    setSelections(resolvedSelections)

    await setDelay(50)

    onEditeAddtional()
  }

  return (
    <div className="rounded-lg border border-orange-100 bg-gradient-to-br from-white via-orange-50 to-orange-100/40 p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex flex-row justify-end mb-2">
        {addtionalsItems.length > 0 && !disableEdition && (
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <button
              disabled={disableEdition}
              onClick={() => setEnableList(true)}
              type="button"
              className="rounded-lg  border-orange-100 bg-orange-300 px-3 py-1 text-xs font-semibold text-black shadow-sm transition-all hover:brightness-110 disabled:opacity-40"
              data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.setEnableListTrue"
            >
              {t("order:label_additionals")}
            </button>
          </Animate>
        )}

        <GetSpace left={5} />

        {selectionsItems.length > 0 && !disableEdition && (
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <button
              disabled={disableEdition}
              onClick={() => setEnableSelectionList(true)}
              type="button"
              className="rounded-lg border-orange-100 bg-orange-300 px-3 py-1 text-xs font-semibold text-black shadow-sm transition-all hover:brightness-110 disabled:opacity-40"
              data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.setEnableSelectionListTrue"
            >
              {t("order:label_options")}
            </button>
          </Animate>
        )}

        <GetSpace left={5} />

        <ButtonIcon
          sizeNumber={SIZE.buttonExpandIcon}
          bgColor={THEME.colors.buttonBGRemoveColor}
          iconColor={THEME.colors.buttonFGRemoveColor}
          typeButton="button"
          icon={faMinus}
          disabled={disableEdition}
          onClick={onRemove}
          descriptionKey="interactionDescriptions:components.elementOrder.elementOrder.button.onRemove"
        />
      </div>

      {hasMissingRequiredSelections && (
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
          <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-red-800 shadow-sm">
            <div className="flex items-start gap-2 max-[700px]:flex-col">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-red-600" />

              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="text-sm font-black">{t("order:title_required_selection_missing")}</p>

                <div className="flex flex-wrap gap-1">
                  {missingRequiredSelections.map((selection) => (
                    <span
                      key={selection.id}
                      className="rounded-full border border-red-200 bg-white px-2 py-1 text-[11px] font-bold text-red-700"
                    >
                      {selection.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Animate>
      )}

      <div className={style.cardRow}>
        {imagen.length > 0 && <img src={imagen} className={style.imageFit} />}

        <div className={cardStyle.card}>
          <div className={cardStyle.cardCollum}>
            <div className={cardStyle.cardContent}>
              <span className={cardStyle.cardTitle}>{title}</span>
            </div>

            <div className={cardStyle.cardContent} style={{ width: "100%" }}>
              <CardEmployeeRegisterInLine
                employee={employeeMemory.findByStringId(employeeStringId)!}
                date={date}
                icon={faUserPen}
              />
            </div>

            <GetSpace top={10} />

            <div className={cardStyle.cardContent}>
              <div className={cardStyle.cardContent} style={{ width: "auto" }}>
                {!calculatePromotions?.hasPromotionAvaliable && (
                  <span className={cardStyle.cardMoneyTxt} style={{ width: "100%" }}>
                    {convertToLocalMoney(genericOrderMeal.valueBase)}

                    {" + "}

                    {convertToLocalMoney(genericOrderMeal?.calculatePromotions?.valueAddtionals)}

                    {" = "}

                    <BasicCard
                      title={convertToLocalMoney(genericOrderMeal.getValueWithAddtions())}
                      borderColor={THEME.colors.white}
                    />
                  </span>
                )}

                <GetSpace left={5} />

                {isClosed && (
                  <BasicCard title={t("order:label_closed_item")} borderColor={THEME.colors.buttonBGSaveColor} />
                )}

                {calculatePromotions?.hasPromotionAvaliable && calculatePromotions?.valueFinal && (
                  <>
                    <span className={cardStyle.cardMoneyTxt} style={{ width: "100%" }}>
                      {convertToLocalMoney(genericOrderMeal.valueBase)}

                      {" + "}

                      {convertToLocalMoney(genericOrderMeal?.calculatePromotions?.valueAddtionals)}

                      {" = "}
                    </span>

                    <span className={`${cardStyle.cardMoneyTxt} ${cardStyle.priceOld}`} style={{ width: "auto" }}>
                      {convertToLocalMoney(
                        genericOrderMeal.valueBase + genericOrderMeal.calculatePromotions?.valueAddtionals!,
                      )}

                      <GetSpace left={10} />
                    </span>

                    <BasicCard title={convertToLocalMoney(calculatePromotions?.valueFinal)} borderColor="#00d14dff" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: "50%", marginLeft: "20px", borderBottom: "solid 1px #2c2c2c0e" }}></div>

      {addtional.length > 0 && (
        <TitleChildrenPath
          boxShadow="none"
          iconColor={THEME.colors.iconBGColor}
          title={t("addtional:title_basic")}
          icon={faCaretDown}
          children={
            <div style={{ padding: "0px 5px 5px 15px" }}>
              <>
                {addtional.map((e, index) => {
                  const additionalDisplay = calculatePromotions?.getDisplayValueByAdditional(e, index)

                  return (
                    <React.Fragment key={index * 1000}>
                      <GetSpace top={5} />

                      <div className={style.cardRow}>
                        {e.addtionalEntity.iconUrl.length > 0 ? (
                          <img src={e.addtionalEntity.iconUrl.toClientRequest()} className={style.imageFitAddtionals} />
                        ) : (
                          <img src={iconMeal} className={style.imageFitAddtionals} />
                        )}

                        <div className={cardStyle.cardContent}>
                          <div className={`${cardStyle.cardContent} flex-wrap items-start`}>
                            <div className={`${cardStyle.cardCollum} flex-1 max-[700px]:basis-full`}>
                              <span className={cardStyle.cardTitle1}>{e.addtionalEntity.name}</span>

                              <span
                                className={cardStyle.cardTxtBody1}
                                style={{ padding: "0px 0px 0px 10px", width: "100%" }}
                              >
                                {e.amount.toString() +
                                  " x " +
                                  e.valueUnique.toString().toMoney() +
                                  " = " +
                                  (additionalDisplay?.valueTotal ?? e.valueTotal).toString().toMoney()}

                                {(additionalDisplay?.freeAmount ?? 0) > 0 &&
                                  ` (${additionalDisplay?.freeAmount} grátis)`}
                              </span>
                            </div>

                            {!disableEdition && (
                              <div className="ml-auto flex w-fit shrink-0 items-center gap-1 rounded-lg border border-orange-100 bg-white p-1 shadow-sm max-[700px]:mt-2 max-[700px]:w-fit">
                                <button
                                  type="button"
                                  onClick={() => handleDecreaseAmount(e)}
                                  className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700 transition hover:bg-red-100 active:scale-95"
                                  title={t("order:label_decrease_additional")}
                                  data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.decreaseAdditional"
                                >
                                  <FontAwesomeIcon icon={faMinus} />
                                </button>

                                <span className="min-w-8 rounded-md bg-slate-50 px-2 text-center text-sm font-black tabular-nums text-slate-900">
                                  {e.amount}
                                </span>

                                <button
                                  type="button"
                                  disabled={isAdditionalStockLimitReached(e.addtionalEntityId, e.amount)}
                                  onClick={() => handleIncreaseAmount(e)}
                                  className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white shadow-sm transition hover:bg-emerald-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                                  title={t("order:label_increase_additional")}
                                  data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.increaseAdditional"
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemove(e)}
                                  className="ml-1 flex h-8 w-8 items-center justify-center rounded-md bg-red-600 text-white shadow-sm transition hover:bg-red-700 active:scale-95"
                                  title={t("order:label_remove_additional")}
                                  data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.removeAdditional"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )
                })}
              </>
            </div>
          }
        />
      )}

      {selections.length > 0 && (
        <TitleChildrenPath
          boxShadow="none"
          iconColor={THEME.colors.iconBGColor}
          title={t("order:label_options")}
          icon={faCaretDown}
          children={
            <div style={{ padding: "0px 5px 5px 15px" }}>
              {selections.map((selection, index) => (
                <React.Fragment key={`${selection.selectionEntityId}-${selection.selectionOptionEntityId}-${index}`}>
                  <GetSpace top={5} />

                  <div className="flex items-center justify-between gap-2 rounded-xl border border-orange-100 bg-white p-2">
                    <div className="flex flex-col w-full">
                      <span className={cardStyle.cardTitle1}>
                        {selection.selectionEntity?.name ?? t("order:label_selection_fallback")}
                      </span>

                      <div className="text-[12px] font-bold text-black ml-4">
                        {selection.selectionOptionEntity?.name ?? t("order:label_option_fallback")}

                        {" - "}

                        {Number(selection.value ?? selection.selectionOptionEntity?.value ?? 0)
                          .toString()
                          .toMoney()}
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={disableEdition}
                      onClick={() =>
                        handleRemoveSelection(selection.selectionEntityId, selection.selectionOptionEntityId)
                      }
                      className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-red-600 text-white shadow-sm transition hover:bg-red-700 active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                      title={t("order:label_remove_selection")}
                      aria-label={t("order:label_remove_selection")}
                      data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.removeSelection"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          }
        />
      )}

      <GetSpace top={10} />

      <div style={{ width: "50%", marginLeft: "20px", borderBottom: "solid 1px #2c2c2c0e" }}></div>

      <GetSpace top={10} />

      {supportsReservation && (
        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="mb-3 rounded-lg border-2 border-orange-200 bg-white p-3">
            <SimpleToggle
              title={t("order:label_reserved_item")}
              value={isReservation}
              boxShadow="none"
              disabled={disableEdition}
              onChange={(checked) => {
                setIsReservation(checked)
                syncDescription(description, checked)
              }}
              descriptionKey="interactionDescriptions:components.elementOrder.elementOrder.toggle.reservedItem"
            />

            {isReservation && (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <InputField
                  type="date"
                  icon={faCalendar}
                  placeholder={t("order:label_reservation_date")}
                  value={reservationDate}
                  disable={disableEdition}
                  onChange={(event) => {
                    const value = event.target.value
                    setReservationDate(value)
                    syncDescription(description, true, value, reservationTime)
                  }}
                  descriptionKey="interactionDescriptions:components.elementOrder.elementOrder.input.reservationDate"
                />

                <InputField
                  type="time"
                  icon={faClock}
                  placeholder={t("order:label_reservation_time")}
                  value={reservationTime}
                  disable={disableEdition}
                  onChange={(event) => {
                    const value = event.target.value
                    setReservationTime(value)
                    syncDescription(description, true, reservationDate, value)
                  }}
                  descriptionKey="interactionDescriptions:components.elementOrder.elementOrder.input.reservationTime"
                />
              </div>
            )}
          </div>
        </Animate>
      )}

      <InputField
        initialValue={genericOrderMeal.description}
        maxLength={INPUT_SIZE.ORDER.genericMealDetails}
        type="text"
        placeholder={t("general:label_observation")}
        icon={faFile}
        disable={disableEdition}
        value={description}
        onChange={(e) => handleChange(e.target.value)}
        descriptionKey="interactionDescriptions:components.elementOrder.elementOrder.input.observation"
      />

      <GetSpace top={10} />

      {calculatePromotions?.hasPromotionAvaliable && calculatePromotions?.valueFinal && (
        <div className={cardStyle.cardContent} style={{ justifyContent: "end", overflowX: "scroll" }}>
          {calculatePromotions?.getValidPromotions().map((e, index) => {
            return (
              <React.Fragment key={index * 2000}>
                <GetSpace left={5} />

                <BasicCard title={e.name + " " + e.getValueToStringMoney()} borderColor={THEME.colors.primary} />
              </React.Fragment>
            )
          })}
        </div>
      )}

      {enableList && (
        <AdditionalSelectionModal
          listCards={addtionalsItems}
          selectedAdditionals={addtional}
          isStockLimitReached={isAdditionalStockLimitReached}
          disableEdition={disableEdition}
          onClose={() => setEnableList(false)}
          onIncrease={handleAddAddtional}
          onSetAmount={handleSetAdditionalAmount}
        />
      )}

      {enableSelectionList && (
        <ListSelections
          icon={faPlusSquare}
          listCards={selectionsItems
            .filter((selection) => selection.status === EEnableStatusType.Active)
            .map((selection, index) => {
              const selectedItem = selections.find((current) => current.selectionEntityId === selection.id)

              const selectedCount = selections.filter((current) => current.selectionEntityId === selection.id).length

              return {
                id: selection.id,
                title: selection.name,
                linkImage: "",
                index,
                dataFilter: [selection.name, selection.description],
                children: (
                  <div className="flex w-full flex-col gap-2 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 max-[700px]:flex-col max-[700px]:items-start">
                      <span className={cardStyle.cardTitle1}>{selection.name}</span>
                      <span className="whitespace-nowrap rounded-full bg-orange-100 px-2 py-1 text-[10px] font-bold text-orange-700 max-[700px]:whitespace-normal">
                        {selectedItem
                          ? selectedCount > 1
                            ? t("order:label_selected_choices_count", { count: selectedCount })
                            : t("order:label_selected_option", {
                                option: selectedItem.selectionOptionEntity?.name ?? t("order:label_option_fallback"),
                              })
                          : t("order:label_not_selected")}
                      </span>
                    </div>

                    <span className={cardStyle.cardTxtBody1}>{selection.description}</span>

                    <div className="flex flex-wrap gap-1">
                      {buildSelectionStatusMessages(selection, selections, t).map((status) => (
                        <span
                          key={`${selection.id}-${status.text}`}
                          className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-black ${status.className}`}
                        >
                          {status.text}
                        </span>
                      ))}
                    </div>

                    <div className="mt-1 flex flex-wrap gap-2">
                      {selection.options.map((option) => {
                        const optionSelectedCount = selections.filter(
                          (current) =>
                            current.selectionEntityId === selection.id && current.selectionOptionEntityId === option.id,
                        ).length

                        const isSelected = optionSelectedCount > 0

                        const selectedCount = selections.filter(
                          (current) => current.selectionEntityId === selection.id,
                        ).length

                        const maximumLimitCandidates = [
                          selection.hasMaximumSelectionsLimit
                            ? Math.max(1, selection.maximumSelectionsLimit ?? 1)
                            : undefined,
                        ].filter((limit): limit is number => typeof limit === "number")

                        const effectiveMaximumSelectionsLimit =
                          maximumLimitCandidates.length > 0 ? Math.min(...maximumLimitCandidates) : undefined

                        const optionAllowsQuantity = selection.allowMultiple && option.allowMultiple

                        const optionMaximumSelectionsLimit = optionAllowsQuantity
                          ? Math.max(1, option.maximumSelectionsLimit ?? 1)
                          : 1

                        const reachedSelectionMaximum =
                          effectiveMaximumSelectionsLimit !== undefined &&
                          selectedCount >= effectiveMaximumSelectionsLimit

                        const reachedOptionMaximum =
                          optionAllowsQuantity && optionSelectedCount >= optionMaximumSelectionsLimit

                        const reachedMaximum = !isSelected && reachedSelectionMaximum

                        const baseValue = option.isFree ? 0 : Number(option.value ?? 0)

                        const selectedWithinSelection = selections.filter(
                          (current) => current.selectionEntityId === selection.id,
                        )

                        const previewSelections = isSelected
                          ? selectedWithinSelection
                          : [
                              ...selectedWithinSelection,
                              {
                                selectionEntityId: selection.id,
                                selectionOptionEntityId: option.id,
                                value: baseValue,
                                baseValue,
                                isFree: option.isFree,
                                selectionOptionEntity: option,
                              },
                            ]

                        const previewValue =
                          resolveSelectionValues(selection, previewSelections).find(
                            (current) => current.selectionOptionEntityId === option.id,
                          )?.value ?? baseValue

                        if (optionAllowsQuantity) {
                          const originallySelectedCount = originalSelections.filter(
                            (current) =>
                              current.selectionEntityId === selection.id && current.selectionOptionEntityId === option.id,
                          ).length

                          const availableStockForThisItem = option.stockQuantity + originallySelectedCount

                          const reachedStockLimit =
                            option.hasStockControl && optionSelectedCount >= availableStockForThisItem

                          const canIncrease =
                            !disableEdition && !reachedSelectionMaximum && !reachedOptionMaximum && !reachedStockLimit
                          const currentOptionTotal = selections
                            .filter(
                              (current) =>
                                current.selectionEntityId === selection.id &&
                                current.selectionOptionEntityId === option.id,
                            )
                            .reduce((sum, current) => sum + Number(current.value ?? 0), 0)
                          const displayValue = optionSelectedCount > 0 ? currentOptionTotal : previewValue

                          const unavailableToAdd = !isSelected && reachedStockLimit

                          return (
                            <div
                              key={option.id}
                              className={`flex min-w-[220px] items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-all max-[700px]:w-full max-[700px]:min-w-0 ${
                                isSelected
                                  ? "border-emerald-600 bg-emerald-50 text-emerald-800 shadow-[0_0_0_2px_#a7f3d0]"
                                  : "border-orange-200 bg-orange-50 text-orange-800"
                              } ${disableEdition || unavailableToAdd ? "cursor-not-allowed opacity-50" : ""}`}
                            >
                              <span className="min-w-0 break-words">
                                {option.name}
                                {optionSelectedCount > 0 ? ` x${optionSelectedCount}` : ""}
                                {unavailableToAdd && (
                                  <span className="ml-1 inline-flex items-center rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                    {t("order:label_option_out_of_stock")}
                                  </span>
                                )}
                              </span>

                              <div className="flex shrink-0 items-center gap-2">
                                <span
                                  className={`rounded-md px-1.5 py-0.5 text-[11px] font-black ${
                                    displayValue <= 0
                                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                      : "bg-orange-50 text-orange-700 ring-1 ring-orange-200"
                                  }`}
                                >
                                  {displayValue <= 0 ? t("order:label_free") : displayValue.toString().toMoney()}
                                </span>

                                <div className="flex items-center gap-1 rounded-lg border border-orange-100 bg-white p-1 shadow-sm">
                                  <button
                                    type="button"
                                    disabled={disableEdition || optionSelectedCount <= 0}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handleRemoveSelection(selection.id, option.id)
                                    }}
                                    className="flex h-7 w-7 items-center justify-center rounded-md bg-red-50 text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                                    data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.event"
                                  >
                                    <FontAwesomeIcon icon={faMinus} />
                                  </button>

                                  <span className="min-w-7 text-center text-xs font-black tabular-nums text-slate-900">
                                    {optionSelectedCount}
                                  </span>

                                  <button
                                    type="button"
                                    disabled={!canIncrease}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handleAddSelectionOption(selection, option.id)
                                    }}
                                    className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                                    data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.event2"
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        }

                        return (
                          <button
                            key={option.id}
                            type="button"
                            disabled={disableEdition || reachedMaximum || option.isOutOfStock}
                            className={`flex min-w-[170px] items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-all max-[700px]:w-full max-[700px]:min-w-0 ${
                              isSelected
                                ? "border-emerald-600 bg-emerald-50 text-emerald-800 shadow-[0_0_0_2px_#a7f3d0]"
                                : "border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100"
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                            onClick={(event) => {
                              event.stopPropagation()
                              handleAddSelectionOption(selection, option.id)
                            }}
                            data-interaction-description-key="interactionDescriptions:components.elementOrder.elementOrder.button.event3"
                          >
                            <span>
                              {option.name}
                              
                              {optionAllowsQuantity && optionSelectedCount > 0 ? ` x${optionSelectedCount}` : ""}
                             
                              {option.isOutOfStock && (
                                <span className="ml-1 inline-flex items-center rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                  {t("order:label_option_out_of_stock")}
                                </span>
                              )}
                            </span>
                            <span
                              className={`rounded-md px-1.5 py-0.5 text-[11px] font-black ${
                                previewValue <= 0
                                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                  : "bg-orange-50 text-orange-700 ring-1 ring-orange-200"
                              }`}
                            >
                              {previewValue <= 0 ? t("order:label_free") : previewValue.toString().toMoney()}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ),
                body: <></>,
              }
            })}
          title={t("order:label_options")}
          messageCount={t("order:label_available_options")}
          messageEmpty={t("order:label_no_available_options")}
          onClinck={() => {}}
          onClonse={() => setEnableSelectionList(false)}
        />
      )}
    </div>
  )
}
